import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    fetchContact,
    fetchEditableContact,
} from 'flow/actions/contact-actions';
import { selectVisitorContactKey } from 'flow/selectors/TicketsSelectors';
import useTicketVisitorContact from 'flow/hooks/useTicketVisitorContact';
import { TicketKey } from 'flow/types/EntityKeys';
import ContactSummaryCard from 'flow/components/tickets/ticket-contact/ContactSummaryCard';
import ContactDetailsCard from 'flow/components/tickets/ticket-contact/ContactDetailsCard';

interface Props {
    ticketKey: TicketKey;
    /**
     * In the docked right panel the cards sit on the panel's gray-100
     * background, so they go one step darker (gray-200, like a selected
     * ticket) for contrast.
     */
    inPanel?: boolean;
}

/**
 * Contact tab for the ticket page. Two cards: avatar + name/secondary
 * (colleagues show their presence profile + a status indicator), then a
 * "Contact details" card with the edit / add-contact action, the contact
 * card's switches for a saved contact, and every populated contact field.
 */
const TicketContactSection = ({ ticketKey, inPanel = false }: Props) => {
    const dispatch = useAppDispatch();
    const { contact, contactIsSaved } = useTicketVisitorContact(ticketKey);
    const contactKey = useAppSelector((state) =>
        selectVisitorContactKey(state, ticketKey),
    );

    useEffect(() => {
        if (contactKey) {
            dispatch(fetchContact(contactKey));
            dispatch(fetchEditableContact(contactKey));
        }
    }, [dispatch, contactKey]);

    return (
        <div className="flex flex-col gap-3">
            <ContactSummaryCard
                ticketKey={ticketKey}
                contact={contact}
                inPanel={inPanel}
            />
            <ContactDetailsCard
                ticketKey={ticketKey}
                contact={contact}
                contactIsSaved={contactIsSaved}
                inPanel={inPanel}
            />
        </div>
    );
};

export default React.memo(TicketContactSection);
